import {
  ENALYSIS_BLOWBY_CATEGORY,
  ENALYSIS_CLEARANCE_ZERO_TO_MAX_GAUGE_BASE_IDS,
  ENALYSIS_METRICS_CATEGORY,
  ENALYSIS_ROD_LOADS_CATEGORY,
  ENALYSIS_TREND_LIST_SOURCE,
  ENALYSIS_TRENDS_LIST_ALLOWED_IDENTIFIERS,
  ENALYSIS_UTILIZATION_CATEGORY,
  ENALYSIS_MIN_DEGREES_REVERSAL_GAUGE_BASE_IDS,
  ENALYSIS_MIN_NET_ROD_LOAD_RATIO_GAUGE_BASE_IDS,
  ENALYSIS_ROD_LOAD_PERCENT_GAUGE_BASE_IDS,
  ENALYSIS_VOLUME_EFFICIENCY_GAUGE_BASE_IDS,
  ENALYSIS_ZERO_TO_HUNDRED_GAUGE_BASE_IDS,
} from "@/constants/enalysis-trends-constants.js";
import { formatEnalysisUnitDisplay } from "@/lib/enalysis-unit-display-utils.js";

const ENALYSIS_GAUGE_BASE_ID_SETS = [
  ENALYSIS_ZERO_TO_HUNDRED_GAUGE_BASE_IDS,
  ENALYSIS_VOLUME_EFFICIENCY_GAUGE_BASE_IDS,
  ENALYSIS_ROD_LOAD_PERCENT_GAUGE_BASE_IDS,
  ENALYSIS_MIN_DEGREES_REVERSAL_GAUGE_BASE_IDS,
  ENALYSIS_MIN_NET_ROD_LOAD_RATIO_GAUGE_BASE_IDS,
  ENALYSIS_CLEARANCE_ZERO_TO_MAX_GAUGE_BASE_IDS,
];

function isEnalysisTrendParam(p) {
  return p?.apiData?.source === ENALYSIS_TREND_LIST_SOURCE;
}

function isAssetTrendsListItem(item) {
  return (
    item != null
    && typeof item === "object"
    && typeof item.trendIdentifier === "string"
    && item.trendIdentifier.trim() !== ""
    && typeof item.trendLabel === "string"
    && item.trendLabel.trim() !== ""
  );
}

function readAssetTrendsListItemUnit(item) {
  const v = item?.unitType ?? item?.unit;
  return typeof v === "string" ? v.trim() : "";
}

export function parseAssetTrendsListResponse(data) {
  if (!Array.isArray(data)) return [];

  const out = [];
  for (const item of data) {
    if (!isAssetTrendsListItem(item)) continue;
    const id = item.trendIdentifier.trim();
    if (!ENALYSIS_TRENDS_LIST_ALLOWED_IDENTIFIERS.has(id)) continue;
    out.push({
      trendIdentifier: id,
      trendLabel: item.trendLabel.trim(),
      trendStageThrow: item.trendStageThrow ?? null,
      listUnit: readAssetTrendsListItemUnit(item),
    });
  }
  return out;
}

function expandedListRow(row, trendIdentifier, trendLabel, stageNum, throwNum) {
  return {
    trendIdentifier,
    trendLabel,
    trendStageThrow: row.trendStageThrow,
    stageNum,
    throwNum,
    baseTrendIdentifier: row.trendIdentifier,
    listUnit: row.listUnit ?? "",
  };
}

function expandTrendListRow(row) {
  const { trendIdentifier: baseId, trendLabel: baseLabel, trendStageThrow: st } =
    row;

  if (!Array.isArray(st) || st.length === 0) {
    return [expandedListRow(row, baseId, baseLabel, null, null)];
  }

  const expanded = [];
  for (const seg of st) {
    if (!seg || typeof seg !== "object") continue;
    const { stageNum, throwNum } = seg;
    if (stageNum == null || throwNum == null) continue;
    const s = String(stageNum);
    const t = String(throwNum);
    expanded.push(
      expandedListRow(
        row,
        `${baseId}_${s}_${t}`,
        `${baseLabel} Stage ${s} Throw ${t}`,
        stageNum,
        throwNum
      )
    );
  }

  return expanded.length > 0
    ? expanded
    : [expandedListRow(row, baseId, baseLabel, null, null)];
}

function resolveEnalysisTagCategory(row) {
  const id = row.trendIdentifier ?? "";
  if (id.includes("MaxPercent")) return ENALYSIS_ROD_LOADS_CATEGORY;
  if (id.includes("Utilized")) return ENALYSIS_UTILIZATION_CATEGORY;
  const base = row.baseTrendIdentifier ?? "";
  if (
    base === "dischargePressure"
    && row.stageNum != null
    && row.throwNum != null
  ) {
    return ENALYSIS_METRICS_CATEGORY;
  }
  if (
    base === "dischargeTemperature"
    && row.stageNum != null
    && row.throwNum != null
  ) {
    return ENALYSIS_METRICS_CATEGORY;
  }
  if (
    base === "suctionPressure"
    && row.stageNum != null
    && row.throwNum != null
  ) {
    return ENALYSIS_METRICS_CATEGORY;
  }
  if (
    ENALYSIS_CLEARANCE_ZERO_TO_MAX_GAUGE_BASE_IDS.has(base)
    && row.stageNum != null
    && row.throwNum != null
  ) {
    return ENALYSIS_METRICS_CATEGORY;
  }
  if (
    base === "blowby"
    && row.stageNum != null
    && row.throwNum != null
  ) {
    return ENALYSIS_BLOWBY_CATEGORY;
  }
  return ENALYSIS_METRICS_CATEGORY;
}

export function mapAssetTrendsListItemsToParameters(items) {
  const expandedRows = items.flatMap(expandTrendListRow);

  const seen = new Set();
  const unique = [];
  for (const row of expandedRows) {
    if (!row.trendIdentifier || seen.has(row.trendIdentifier)) continue;
    seen.add(row.trendIdentifier);
    unique.push(row);
  }

  return unique.map((row) => {
    const category = resolveEnalysisTagCategory(row);
    const useGauge =
      category === ENALYSIS_UTILIZATION_CATEGORY
      || ENALYSIS_GAUGE_BASE_ID_SETS.some((set) =>
        set.has(row.baseTrendIdentifier)
      );
    return {
      parameter: row.trendLabel,
      unit: formatEnalysisUnitDisplay(row.listUnit ?? ""),
      displayType: useGauge ? "Gauge" : "Text",
      universalDescriptorId: null,
      alarmSetPoints: { lolo: null, lo: null, hi: null, hihi: null },
      status: "Good",
      currentValue: "—",
      category,
      apiData: {
        source: ENALYSIS_TREND_LIST_SOURCE,
        trendIdentifier: row.trendIdentifier,
        baseTrendIdentifier: row.baseTrendIdentifier,
        tagKey: row.trendIdentifier,
        trendStageThrow: row.trendStageThrow,
        stageNum: row.stageNum,
        throwNum: row.throwNum,
      },
    };
  });
}

export function getCommaSeparatedKeysForEnalysisTrendsData(parameters) {
  if (!Array.isArray(parameters) || parameters.length === 0) return "";
  const ids = [
    ...new Set(
      parameters
        .filter(isEnalysisTrendParam)
        .map((p) => p.apiData.trendIdentifier)
        .filter(Boolean)
    ),
  ];
  return ids.join(",");
}

export function mapEnalysisTrendDataPointsToMeasurements(dataPoints) {
  if (!Array.isArray(dataPoints)) return [];
  const rows = [];
  for (const dp of dataPoints) {
    const reportDate = dp?.reportDate;
    const rawVal = dp?.value;
    if (reportDate == null || rawVal == null) continue;
    const at = new Date(String(reportDate).trim());
    if (Number.isNaN(at.getTime())) continue;
    const DateTime = at.toISOString();
    const num =
      typeof rawVal === "number"
        ? rawVal
        : parseFloat(String(rawVal).replace(/,/g, ""));
    if (!Number.isFinite(num)) continue;
    rows.push({ DateTime, Value: num });
  }
  rows.sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
  return rows;
}

export function filterEnalysisMeasurementsByTrendRange(
  measurements,
  cacheKey,
  customStartDate,
  customEndDate
) {
  if (!Array.isArray(measurements) || measurements.length === 0) return [];

  if (typeof cacheKey === "string" && cacheKey.startsWith("custom-")) {
    let startStr = customStartDate;
    let endStr = customEndDate;
    const customMatch = cacheKey.match(
      /^custom-(\d{4}-\d{2}-\d{2})-(\d{4}-\d{2}-\d{2})$/
    );
    if (customMatch) {
      startStr = customMatch[1];
      endStr = customMatch[2];
    }
    if (!startStr || !endStr) return measurements.slice();
    const startMs = new Date(`${startStr}T00:00:00`).getTime();
    const endMs = new Date(`${endStr}T23:59:59.999`).getTime();
    return measurements.filter((m) => {
      const t = new Date(m.DateTime).getTime();
      return t >= startMs && t <= endMs;
    });
  }

  const windowMs =
    cacheKey === "24h"
      ? 24 * 60 * 60 * 1000
      : cacheKey === "7d"
        ? 7 * 24 * 60 * 60 * 1000
        : cacheKey === "30d"
          ? 30 * 24 * 60 * 60 * 1000
          : null;

  if (windowMs == null) return measurements.slice();

  const times = measurements
    .map((m) => new Date(m.DateTime).getTime())
    .filter((t) => Number.isFinite(t));
  const endMs = times.length > 0 ? Math.max(...times) : Date.now();
  const startMs = endMs - windowMs;
  return measurements.filter((m) => {
    const t = new Date(m.DateTime).getTime();
    return t >= startMs && t <= endMs;
  });
}

function pickTrendDisplayValue(v) {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "string") return v;
  if (typeof v !== "object") return null;
  if (v.value != null) return pickTrendDisplayValue(v.value);
  const dps = v.dataPoints;
  if (!Array.isArray(dps) || dps.length === 0) return null;
  const measurements = mapEnalysisTrendDataPointsToMeasurements(dps);
  if (measurements.length === 0) return null;
  return String(measurements[measurements.length - 1].Value);
}

function readStageThrowCoords(o) {
  const s = Number(o?.stageNum);
  const t = Number(o?.throwNum);
  if (!Number.isFinite(s) || !Number.isFinite(t)) return null;
  return { stage: s, throw: t };
}

function filterEnalysisDataPointsByStageThrow(dataPoints, stageNum, throwNum) {
  if (!Array.isArray(dataPoints) || dataPoints.length === 0) return dataPoints;
  if (stageNum == null || throwNum == null) return dataPoints;
  const s = Number(stageNum);
  const t = Number(throwNum);
  if (!Number.isFinite(s) || !Number.isFinite(t)) return dataPoints;
  if (!dataPoints.some((dp) => readStageThrowCoords(dp) != null)) {
    return dataPoints;
  }
  return dataPoints.filter((dp) => {
    const c = readStageThrowCoords(dp);
    return c != null && c.stage === s && c.throw === t;
  });
}

function pickTrendValueForStageThrow(entry, stageNum, throwNum) {
  if (entry == null) return null;
  if (stageNum == null || throwNum == null) {
    return pickTrendDisplayValue(entry);
  }
  if (Array.isArray(entry)) {
    const s = Number(stageNum);
    const t = Number(throwNum);
    const match = entry.find((pt) => {
      const c = readStageThrowCoords(pt);
      return c != null && c.stage === s && c.throw === t;
    });
    if (match) return pickTrendDisplayValue(match);
    return null;
  }
  return pickTrendDisplayValue(entry);
}

function seriesListFromTrendsData(trendsData) {
  if (Array.isArray(trendsData)) {
    return trendsData.filter((x) => x != null && typeof x === "object");
  }
  const inner = trendsData?.data;
  return Array.isArray(inner) ? seriesListFromTrendsData(inner) : [];
}

function readTrendSeriesLookupId(item) {
  const id = item?.trendIdentifier ?? item?.key;
  return typeof id === "string" ? id.trim() : "";
}

function lookupTrend(lookup, id) {
  return id ? lookup[id] : undefined;
}

function buildEnalysisParameterIndexToSeriesMap(parameters, seriesList) {
  const map = new Map();
  if (!Array.isArray(parameters) || !Array.isArray(seriesList)) return map;

  const queues = new Map();
  for (let i = 0; i < parameters.length; i++) {
    if (!isEnalysisTrendParam(parameters[i])) continue;
    const p = parameters[i];
    const base = String(p.apiData?.baseTrendIdentifier ?? "").trim();
    const full = String(p.apiData?.trendIdentifier ?? "").trim();
    const id = base || full;
    if (!id) continue;
    if (!queues.has(id)) queues.set(id, []);
    queues.get(id).push(i);
  }

  for (const item of seriesList) {
    const k = readTrendSeriesLookupId(item);
    if (!k || !queues.has(k)) continue;
    const q = queues.get(k);
    if (q.length === 0) continue;
    map.set(q.shift(), item);
  }
  return map;
}

function buildTrendsDataLookupFromSeriesList(list) {
  const out = {};
  for (const item of list) {
    const baseId = readTrendSeriesLookupId(item);
    if (!baseId) continue;
    const stTh = readStageThrowCoords(item);
    if (stTh) {
      const suffix = `_${stTh.stage}_${stTh.throw}`;
      const key = baseId.endsWith(suffix) ? baseId : `${baseId}${suffix}`;
      out[key] = item;
    } else {
      out[baseId] = item;
    }
  }
  return out;
}

function resolveTrendEntry(parameterIndex, full, base, byIndex, lookup) {
  return (
    byIndex.get(parameterIndex)
    ?? (full ? lookupTrend(lookup, full) : undefined)
    ?? lookupTrend(lookup, base)
  );
}

function unitFromTrendSeries(entry) {
  const u = entry?.unitType;
  const raw = typeof u === "string" ? u.trim() : "";
  return formatEnalysisUnitDisplay(raw);
}

function parameterWithEnalysisMeasurements(p, entry, measurements) {
  const last = measurements[measurements.length - 1];
  const legendName = entry.legendName && String(entry.legendName).trim();
  const ut = unitFromTrendSeries(entry);
  return {
    ...p,
    currentValue: String(last.Value),
    unit: ut || p.unit,
    apiData: {
      ...p.apiData,
      enalysisTrendRaw: { Measurements: measurements },
      ...(legendName ? { enalysisTrendLegendName: legendName } : {}),
    },
  };
}

export function mergeEnalysisTrendsDataIntoParameters(parameters, trendsData) {
  if (!Array.isArray(parameters) || parameters.length === 0) return parameters;
  const seriesList = seriesListFromTrendsData(trendsData);
  const byIndex = buildEnalysisParameterIndexToSeriesMap(parameters, seriesList);
  const lookup = buildTrendsDataLookupFromSeriesList(seriesList);

  return parameters.map((p, parameterIndex) => {
    if (!isEnalysisTrendParam(p)) return p;
    const full = String(p.apiData.trendIdentifier ?? "");
    const base = String(p.apiData.baseTrendIdentifier ?? "") || full;
    if (!base) return p;

    const entry = resolveTrendEntry(
      parameterIndex,
      full,
      base,
      byIndex,
      lookup
    );
    if (entry == null) return p;

    const hasStageThrow =
      p.apiData?.stageNum != null && p.apiData?.throwNum != null;
    const dataPoints = entry.dataPoints;

    if (Array.isArray(dataPoints) && dataPoints.length > 0) {
      const slice = filterEnalysisDataPointsByStageThrow(
        dataPoints,
        p.apiData.stageNum,
        p.apiData.throwNum
      );
      const measurements = mapEnalysisTrendDataPointsToMeasurements(slice);
      if (measurements.length > 0) {
        return parameterWithEnalysisMeasurements(p, entry, measurements);
      }
    }

    const text =
      hasStageThrow || full === base
        ? pickTrendValueForStageThrow(
            entry,
            p.apiData.stageNum,
            p.apiData.throwNum
          )
        : pickTrendDisplayValue(entry);
    if (text == null) return p;
    const ut = unitFromTrendSeries(entry);
    return { ...p, currentValue: text, unit: ut || p.unit };
  });
}

export function mapEnalysisAssetTrendsListResponseToParameters(data) {
  const parsed = parseAssetTrendsListResponse(data);
  if (parsed.length > 0) {
    return mapAssetTrendsListItemsToParameters(parsed);
  }
  return mapEnalysisTrendTagNamesToParameters(
    extractTagNamesFromAssetTrendsListResponse(data)
  );
}

export function extractTagNamesFromAssetTrendsListResponse(data) {
  if (data == null) return [];

  if (typeof data === "string" && data.trim()) {
    return [data.trim()];
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return [];
    if (typeof data[0] === "string") {
      return data.map((s) => String(s).trim()).filter(Boolean);
    }
    const out = [];
    for (const item of data) {
      if (typeof item === "string" && item.trim()) {
        out.push(item.trim());
        continue;
      }
      const id = item?.trendIdentifier ?? item?.key;
      if (typeof id === "string" && id.trim()) out.push(id.trim());
    }
    return out;
  }

  if (typeof data === "object" && data.data != null) {
    return extractTagNamesFromAssetTrendsListResponse(data.data);
  }

  return [];
}

export function mapEnalysisTrendTagNamesToParameters(tagNames) {
  const seen = new Set();
  const unique = [];
  for (const name of tagNames) {
    if (!name || seen.has(name)) continue;
    seen.add(name);
    unique.push(name);
  }

  return unique.map((parameter) => ({
    parameter,
    unit: "",
    displayType: "Text",
    universalDescriptorId: null,
    alarmSetPoints: { lolo: null, lo: null, hi: null, hihi: null },
    status: "Good",
    currentValue: "—",
    category: ENALYSIS_METRICS_CATEGORY,
    apiData: {
      source: ENALYSIS_TREND_LIST_SOURCE,
      tagKey: parameter,
    },
  }));
}
